import request from '../../utils/request';
import type { Result } from '../../models/general';
import { apiErrorsOf, httpStatusOf } from '../../utils/requestHandling';
import type { CompanyModuleConfigView } from './moduleConfig';

/**
 * Operator-only calls of the Module Configuration editor (`/settings/module-config`).
 *
 * This file exists so the editor's calls stay out of `moduleConfig.ts`, which the app shell reads on
 * every page load (nav config, capabilities) and which another lane owns. Everything here is a
 * deliberate cross-tenant read or write by the Operator: it carries a company id, a company name and
 * config rows, never employee or HR data.
 */

/**
 * A company's config as the editor needs it: the stored rows plus the version they were read at.
 *
 * `version` is an opaque token the server computes from the rows (CO-860). A save sends it back, and
 * the server refuses the save with 409 when the rows have changed since.
 */
export interface CompanyConfigForEdit extends CompanyModuleConfigView {
  version: string;
}

/**
 * Outcome of the edit read.
 *
 * `ok: false` is its own branch, never an empty config: an empty config renders as every switch ON,
 * complete and editable, over a tenant whose real state was never read. `failure` is diagnostic text
 * (server description or thrown message), not display copy; `status` is the transport status, null
 * when the request never got one.
 */
export type CompanyConfigForEditResult =
  | { ok: true; config: CompanyConfigForEdit }
  | { ok: false; failure: string; status: number | null };

/** One changed module switch. `displayOrder` is used only when the server has to insert the row. */
export interface ModuleConfigDeltaItem {
  moduleId: string;
  isEnabled: boolean;
  displayOrder?: number;
}

/**
 * One changed page, tab, capability or mobile-page switch. `moduleId` is the registry's id; for a
 * web page (a path starting with a slash) the server applies the value to every row holding that
 * path and uses `moduleId` only when it has to insert one.
 */
export interface PageConfigDeltaItem {
  moduleId: string;
  pagePath: string;
  isEnabled: boolean;
}

/** Body of `PATCH navigation/config/company/{id}`: the changed keys only. */
export interface CompanyConfigDelta {
  /** The `version` of the read these changes were staged against. */
  version: string;
  modules: ModuleConfigDeltaItem[];
  pages: PageConfigDeltaItem[];
}

/**
 * Outcome of a delta save.
 *
 *  - `rejected`: the server answered and refused (400, 401, 403, 404). The save is one transaction,
 *    so nothing was written. `reason` is the server's own description when it gave one.
 *  - `conflict`: 409. The stored rows changed after the read `version` belongs to. Nothing written.
 *  - `unknown`: no answer, a 5xx, or a 2xx whose body could not be read as a config. The write may
 *    or may not have happened; only a fresh read can tell.
 *
 * `failure` is diagnostic text for the console, never display copy.
 */
export type PatchCompanyConfigResult =
  | { ok: true; config: CompanyConfigForEdit }
  | {
      ok: false;
      outcome: 'rejected' | 'conflict' | 'unknown';
      status: number | null;
      reason: string | null;
      failure: string;
    };

function describeRejection(error: unknown, status: number | null): string {
  if (error instanceof Error && error.message) return error.message;
  const first = apiErrorsOf(error)[0]?.description;
  if (first) return first;
  if (typeof error === 'string' && error) return error;
  return status !== null ? `HTTP ${status}` : 'request rejected with no readable reason';
}

/** The payload as an editable config, or null when a list or the version is missing. */
function readConfig(payload: CompanyModuleConfigView | null | undefined): CompanyConfigForEdit | null {
  if (!payload || !Array.isArray(payload.modules) || !Array.isArray(payload.pages)) return null;
  const version = (payload as Partial<CompanyConfigForEdit>).version;
  if (typeof version !== 'string' || version.length === 0) return null;
  return { ...payload, version };
}

/**
 * Module rows AND page rows of one company in a single request.
 *
 * `GET navigation/modules/company/{id}` has always returned both lists; the editor used to ignore
 * `pages` and fetch them with a second call whose failure was swallowed into `[]`, which rendered
 * every page, tab and capability as ON over a tenant that had some OFF. One request has one outcome.
 * A body without `modules`, without `pages` or without `version` is a failure for the same reason: a
 * screen that cannot say which version it shows cannot be saved safely.
 */
export async function getCompanyConfigForEdit(companyId: number): Promise<CompanyConfigForEditResult> {
  try {
    const response = await request.get<Result<CompanyModuleConfigView>>(
      `navigation/modules/company/${companyId}`
    );
    const body = response.data;
    if (!body) {
      return { ok: false, failure: 'empty response body', status: response.status ?? null };
    }
    // Key Constraint 4: errors before payload.
    if (body.errors?.length) {
      const detail = body.errors[0]?.description || `status ${body.status}`;
      return { ok: false, failure: detail, status: response.status ?? null };
    }
    const config = readConfig(body.payload);
    if (!config) {
      return { ok: false, failure: 'response carried no module list, page list or version', status: response.status ?? null };
    }
    return { ok: true, config };
  } catch (error) {
    const status = httpStatusOf(error) ?? null;
    const failure = describeRejection(error, status);
    console.error('Failed to fetch company config for edit:', status ?? 'no status', failure, error);
    return { ok: false, failure, status };
  }
}

/**
 * Save the changed keys of one company in a single request and a single transaction (CO-860).
 *
 * The server applies exactly the keys in `delta`, deletes nothing, and answers with the config as it
 * now stands, which becomes the editor's new baseline. This replaces the two replace-all PUTs for
 * this screen; those stay in `moduleConfig.ts` for their other callers.
 */
export async function patchCompanyConfig(
  companyId: number,
  delta: CompanyConfigDelta
): Promise<PatchCompanyConfigResult> {
  try {
    const response = await request.patch<Result<CompanyModuleConfigView>>(
      `navigation/config/company/${companyId}`,
      delta
    );
    const status = response.status ?? null;
    const body = response.data;
    // Key Constraint 4: errors before payload.
    if (body?.errors?.length) {
      const reason = body.errors[0]?.description || null;
      return { ok: false, outcome: 'rejected', status, reason, failure: reason ?? `status ${body.status}` };
    }
    const config = readConfig(body?.payload);
    if (!config) {
      return {
        ok: false,
        outcome: 'unknown',
        status,
        reason: null,
        failure: 'the save was answered without a readable configuration',
      };
    }
    return { ok: true, config };
  } catch (error) {
    const status = httpStatusOf(error) ?? null;
    const reason = apiErrorsOf(error)[0]?.description || null;
    const failure = describeRejection(error, status);
    console.error('Failed to save company config delta:', status ?? 'no status', failure, error);
    if (status === 409) return { ok: false, outcome: 'conflict', status, reason, failure };
    if (status === null || status >= 500) return { ok: false, outcome: 'unknown', status, reason, failure };
    return { ok: false, outcome: 'rejected', status, reason, failure };
  }
}

/* ── Demo surfaces (CO-849 grants, CO-861 list, CO-862 live flag). Operator only. ── */

/** What a demo surface is: a web page (key = its path), a tab (key = its tab id) or a whole module. */
export type DemoSurfaceKind = 'page' | 'tab' | 'module';

/** Names one demo surface. `moduleId` is read for a tab only (its tab group id). */
export interface DemoSurfaceRef {
  surfaceKind: DemoSurfaceKind;
  surfaceKey: string;
  moduleId?: string;
}

/** One key of the demo list and whether the company holds an active grant for it. */
export interface DemoSurfaceGrant {
  surfaceKind: DemoSurfaceKind;
  surfaceKey: string;
  /** Registry module id of a page, tab group id of a tab, the module's own id for a module. */
  moduleId: string;
  isGranted: boolean;
  /** UTC. Absent when not granted (the API drops null members). */
  grantedAtUtc?: string;
  note?: string;
}

/** The whole demo list for one company, as `demo-grants/company/{id}` answers on GET, PUT and DELETE. */
export interface CompanyDemoGrants {
  companyId: number;
  companyName: string;
  surfaces: DemoSurfaceGrant[];
}

/** The demo list itself (`GET navigation/demo-surfaces`): no company, no database read. */
export interface DemoSurfaceList {
  moduleIds: string[];
  /** A page is matched by `pagePath` alone; a tab (`isTab`, `pagePath` = bare tab id) by the pair. */
  pages: { pagePath: string; moduleId: string; isTab: boolean }[];
}

/** One surface of `demo-exposure/company/{id}`. Only granted or stored-ON keys are listed there. */
export interface DemoExposureSurface {
  surfaceKind: DemoSurfaceKind;
  surfaceKey: string;
  moduleId: string;
  isGranted: boolean;
  grantedAtUtc?: string;
  isVisible: boolean;
  hiddenByModuleId?: string;
  stored: 'on' | 'off' | 'mixed' | 'none';
}

/**
 * What the server says about one company and its demo surfaces (`GET demo-exposure/company/{id}`).
 *
 * `isLive` is the owner's definition (2026-10-13): not archived, not a test company and not on the
 * compiled demo-tenant list. It is the one source for "does granting here need a confirmation".
 */
export interface CompanyDemoExposure {
  companyId: number;
  companyName: string;
  tier: 'free' | 'trial' | 'paid';
  isTestCompany: boolean;
  isDemoTenant: boolean;
  isLive: boolean;
  visibleCount: number;
  grantedCount: number;
  staleOnCount: number;
  lastChangedUtc?: string;
  surfaces: DemoExposureSurface[];
}

/**
 * Outcome of one of the demo calls. A failure is its own branch and never an empty list: an empty
 * demo list would render every demo switch as an ordinary one. `reason` is the server's own
 * description of a refusal when it gave one; `failure` is diagnostic text for the console.
 */
export type DemoCallResult<T> =
  | { ok: true; value: T }
  | { ok: false; status: number | null; reason: string | null; failure: string };

async function demoCall<T>(
  label: string,
  send: () => Promise<{ data: Result<T>; status?: number }>,
  isComplete: (payload: T) => boolean
): Promise<DemoCallResult<T>> {
  try {
    const response = await send();
    const status = response.status ?? null;
    const body = response.data;
    // Key Constraint 4: errors before payload.
    if (body?.errors?.length) {
      const reason = body.errors[0]?.description || null;
      return { ok: false, status, reason, failure: reason ?? `status ${body.status}` };
    }
    const payload = body?.payload;
    if (!payload || !isComplete(payload)) {
      return { ok: false, status, reason: null, failure: `${label}: the answer carried no readable payload` };
    }
    return { ok: true, value: payload };
  } catch (error) {
    const status = httpStatusOf(error) ?? null;
    const reason = apiErrorsOf(error)[0]?.description || null;
    const failure = describeRejection(error, status);
    console.error(`${label} failed:`, status ?? 'no status', failure, error);
    return { ok: false, status, reason, failure };
  }
}

const hasSurfaces = (payload: { surfaces?: unknown }) => Array.isArray(payload.surfaces);

/** The demo list. The per-company screens read `getCompanyDemoGrants` instead, which carries it. */
export function getDemoSurfaces(): Promise<DemoCallResult<DemoSurfaceList>> {
  return demoCall(
    'Demo surface list',
    () => request.get<Result<DemoSurfaceList>>('navigation/demo-surfaces'),
    payload => Array.isArray(payload.moduleIds) && Array.isArray(payload.pages)
  );
}

/** Every demo key, each marked granted or not for this company. */
export function getCompanyDemoGrants(companyId: number): Promise<DemoCallResult<CompanyDemoGrants>> {
  return demoCall(
    'Demo grants read',
    () => request.get<Result<CompanyDemoGrants>>(`navigation/demo-grants/company/${companyId}`),
    hasSurfaces
  );
}

/**
 * Grant ONE demo surface to one company. The grant alone lights the surface for the company's users,
 * at once; no config row is written. Idempotent. Answers the company's list as it now stands.
 */
export function grantDemoSurface(
  companyId: number,
  surface: DemoSurfaceRef,
  note?: string
): Promise<DemoCallResult<CompanyDemoGrants>> {
  return demoCall(
    'Demo grant',
    () =>
      request.put<Result<CompanyDemoGrants>>(`navigation/demo-grants/company/${companyId}`, {
        surfaceKind: surface.surfaceKind,
        surfaceKey: surface.surfaceKey,
        ...(surface.moduleId ? { moduleId: surface.moduleId } : {}),
        ...(note ? { note } : {}),
      }),
    hasSurfaces
  );
}

/** Revoke ONE demo surface: the company's users lose it at once. Idempotent; the grant row is kept, stamped. */
export function revokeDemoSurface(
  companyId: number,
  surface: DemoSurfaceRef
): Promise<DemoCallResult<CompanyDemoGrants>> {
  return demoCall(
    'Demo revoke',
    () =>
      request.delete<Result<CompanyDemoGrants>>(`navigation/demo-grants/company/${companyId}`, {
        params: {
          surfaceKind: surface.surfaceKind,
          surfaceKey: surface.surfaceKey,
          ...(surface.moduleId ? { moduleId: surface.moduleId } : {}),
        },
      }),
    hasSurfaces
  );
}

/**
 * One company's header (tier, test flag, demo-tenant flag, `isLive`) and its demo exposure.
 * 404 for an unknown or archived company. A failure here must be read as "not known", never as
 * "not live": the caller asks for the confirmation when it cannot tell.
 */
export function getCompanyDemoExposure(companyId: number): Promise<DemoCallResult<CompanyDemoExposure>> {
  return demoCall(
    'Demo exposure read',
    () => request.get<Result<CompanyDemoExposure>>(`navigation/demo-exposure/company/${companyId}`),
    payload => typeof payload.isLive === 'boolean' && hasSurfaces(payload)
  );
}

/* ── Fleet view, drift, safe defaults and the stored-row remove (CO-862). Operator only. ── */

/**
 * `GET navigation/demo-exposure`: every company and the demo surfaces its users can see.
 *
 * A company's `surfaces` lists ONLY the keys that are granted or hold a stored ON row; a key that is
 * not listed is hidden. Archived companies are never returned. Demo tenants come back with `isLive`
 * false and are not in the two live counts. Sorted by the server: most visible first, then by name.
 */
export interface DemoExposureFleet {
  /** Number of keys on the demo list today. */
  surfaceCount: number;
  includesTestCompanies: boolean;
  liveCompanyCount: number;
  exposedLiveCompanyCount: number;
  companies: CompanyDemoExposure[];
}

/** What the Operator is told about a company on the fleet endpoints: platform facts only. */
export type DemoFleetCompany = Pick<
  CompanyDemoExposure,
  'companyId' | 'companyName' | 'tier' | 'isTestCompany' | 'isDemoTenant' | 'isLive'
>;

/** A demo key as the demo list spells it. */
export interface DemoSurfaceKeyRef {
  surfaceKind: DemoSurfaceKind;
  surfaceKey: string;
  moduleId: string;
}

/** One stored row named by the drift read. `rowModuleId` is the id the ROW is stored under. */
export interface DemoDriftRow extends DemoSurfaceKeyRef {
  rowModuleId: string;
  isEnabled: boolean;
  updatedAt: string;
}

/** An active grant whose key has left the demo list. Revoke it by this stored spelling. */
export interface DemoDriftGrant extends DemoSurfaceKeyRef {
  grantedAtUtc: string;
}

/**
 * Where one company's stored state differs from the safe default. None of it changes what the
 * company's users see (the grant decides, CO-849).
 */
export interface DemoDriftCompany extends DemoFleetCompany {
  staleOnCount: number;
  missingRowCount: number;
  legacyRowCount: number;
  orphanGrantCount: number;
  /** Stored ON rows for a key the company holds no grant for. */
  staleOn: DemoDriftRow[];
  /** Keys with no stored row and no grant. Filled on the one-company read only. */
  missingRows: DemoSurfaceKeyRef[];
  /** Rows for a demo page stored under a module id other than the demo list's (CO-496). */
  legacyRows: DemoDriftRow[];
  orphanGrants: DemoDriftGrant[];
}

/** `GET navigation/demo-drift`: only the companies with something to report. */
export interface DemoDriftFleet {
  surfaceCount: number;
  includesTestCompanies: boolean;
  /** Companies looked at, with or without drift. */
  companyCount: number;
  companies: DemoDriftCompany[];
}

/** Body of the safe-defaults preview and apply. There is no member that could switch anything on. */
export interface DemoSafeDefaultsRequest {
  /** 1 to 500 ids, each an existing company that is not archived. */
  companyIds: number[];
  /** Limit the run to these demo keys. Left out = the whole demo list. */
  surfaces?: DemoSurfaceRef[];
}

/** One stored row safe defaults changes, or would change. */
export interface DemoSafeDefaultsChange extends DemoSurfaceKeyRef {
  rowModuleId: string;
  /** `switchOff`: an ON row goes OFF. `insertOff`: the company has no row, so the OFF row is added. */
  action: 'switchOff' | 'insertOff';
}

export interface DemoSafeDefaultsCompany extends DemoFleetCompany {
  changes: DemoSafeDefaultsChange[];
  /** Keys left alone because the company holds an active grant for them. */
  notTouched: DemoSurfaceKeyRef[];
  /** Apply only: this company's rows were saved. False on a preview and when nothing had to change. */
  applied: boolean;
  /** Apply only: this company's save failed and none of its rows were written. */
  error?: string;
}

/**
 * Answer of the preview and of the apply. The apply answers 200 even when one company failed: read
 * `failedCompanyCount` and each company's `error`.
 */
export interface DemoSafeDefaultsResult {
  committed: boolean;
  changeCount: number;
  failedCompanyCount: number;
  companies: DemoSafeDefaultsCompany[];
}

/** The server accepts at most this many company ids in one safe-defaults request. */
export const SAFE_DEFAULTS_MAX_COMPANIES = 500;

const hasCompanies = (payload: { companies?: unknown }) => Array.isArray(payload.companies);

/**
 * The fleet read: which companies see which demo surfaces. One request, loaded once per visit and on
 * demand; never poll it (every SQL command on production is a paid telemetry row).
 */
export function getDemoExposureFleet(includeTest: boolean): Promise<DemoCallResult<DemoExposureFleet>> {
  return demoCall(
    'Demo exposure fleet read',
    () => request.get<Result<DemoExposureFleet>>('navigation/demo-exposure', { params: { includeTest } }),
    payload => hasCompanies(payload) && typeof payload.surfaceCount === 'number'
  );
}

/** Drift across the fleet: only companies with drift, `missingRows` left empty (its count is filled). */
export function getDemoDriftFleet(includeTest: boolean): Promise<DemoCallResult<DemoDriftFleet>> {
  return demoCall(
    'Demo drift fleet read',
    () => request.get<Result<DemoDriftFleet>>('navigation/demo-drift', { params: { includeTest } }),
    hasCompanies
  );
}

/** Drift of one company, always returned, with `missingRows` filled. 404 for an unknown company. */
export function getCompanyDemoDrift(companyId: number): Promise<DemoCallResult<DemoDriftCompany>> {
  return demoCall(
    'Demo drift read',
    () => request.get<Result<DemoDriftCompany>>(`navigation/demo-drift/company/${companyId}`),
    payload =>
      Array.isArray(payload.staleOn) && Array.isArray(payload.legacyRows) && Array.isArray(payload.orphanGrants)
  );
}

const safeDefaultsBody = (input: DemoSafeDefaultsRequest) => ({
  companyIds: input.companyIds,
  ...(input.surfaces && input.surfaces.length > 0
    ? {
        surfaces: input.surfaces.map(surface => ({
          surfaceKind: surface.surfaceKind,
          surfaceKey: surface.surfaceKey,
          ...(surface.moduleId ? { moduleId: surface.moduleId } : {}),
        })),
      }
    : {}),
});

/** What safe defaults WOULD change, per company. Writes nothing. */
export function previewDemoSafeDefaults(
  input: DemoSafeDefaultsRequest
): Promise<DemoCallResult<DemoSafeDefaultsResult>> {
  return demoCall(
    'Safe defaults preview',
    () =>
      request.post<Result<DemoSafeDefaultsResult>>('navigation/demo-safe-defaults/preview', safeDefaultsBody(input)),
    hasCompanies
  );
}

/**
 * Apply safe defaults: stored rows only, OFF only (switch an ON row off, or add the OFF row), never a
 * delete, never a grant. It skips every granted key, so it does NOT take a demo surface away from
 * anyone: that is a revoke. One transaction per company; a failed company is reported in the answer.
 */
export function applyDemoSafeDefaults(
  input: DemoSafeDefaultsRequest
): Promise<DemoCallResult<DemoSafeDefaultsResult>> {
  return demoCall(
    'Safe defaults apply',
    () =>
      request.post<Result<DemoSafeDefaultsResult>>('navigation/demo-safe-defaults/apply', safeDefaultsBody(input)),
    payload => hasCompanies(payload) && payload.committed === true
  );
}

/** Names one stored row: a module row by its id, a page row by (module id, page path). */
export type StoredRowRef =
  | { kind: 'module'; moduleId: string }
  | { kind: 'page'; moduleId: string; pagePath: string };

/**
 * Remove ONE stored row on purpose (`DELETE navigation/config/company/{id}/row`).
 *
 * The server refuses (400, reason given) the HR-owned chief self-service row and a stored OFF row,
 * because a missing row reads as ON; it answers 404 when the row is gone and 409 when the stored rows
 * changed since `version` was read. On success it answers the company's config with its NEW version:
 * whoever holds the old version must re-read before the next save, or that save answers 409.
 */
export async function removeStoredConfigRow(
  companyId: number,
  row: StoredRowRef,
  version: string
): Promise<PatchCompanyConfigResult> {
  try {
    const response = await request.delete<Result<CompanyModuleConfigView>>(
      `navigation/config/company/${companyId}/row`,
      {
        params: {
          kind: row.kind,
          moduleId: row.moduleId,
          ...(row.kind === 'page' ? { pagePath: row.pagePath } : {}),
          version,
        },
      }
    );
    const status = response.status ?? null;
    const body = response.data;
    // Key Constraint 4: errors before payload.
    if (body?.errors?.length) {
      const reason = body.errors[0]?.description || null;
      return { ok: false, outcome: 'rejected', status, reason, failure: reason ?? `status ${body.status}` };
    }
    const config = readConfig(body?.payload);
    if (!config) {
      return {
        ok: false,
        outcome: 'unknown',
        status,
        reason: null,
        failure: 'the remove was answered without a readable configuration',
      };
    }
    return { ok: true, config };
  } catch (error) {
    const status = httpStatusOf(error) ?? null;
    const reason = apiErrorsOf(error)[0]?.description || null;
    const failure = describeRejection(error, status);
    console.error('Failed to remove a stored config row:', status ?? 'no status', failure, error);
    if (status === 409) return { ok: false, outcome: 'conflict', status, reason, failure };
    if (status === null || status >= 500) return { ok: false, outcome: 'unknown', status, reason, failure };
    return { ok: false, outcome: 'rejected', status, reason, failure };
  }
}
